"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Returns true only when `isLoading` has been true for at least `delayMs`.
 * Prevents skeleton/loader flash when data resolves quickly.
 */
export function useDeferredLoading(isLoading: boolean, delayMs = 200): boolean {
    const [showSkeleton, setShowSkeleton] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!isLoading) {
            clearTimer();
            // Use a 0ms timeout to avoid synchronous setState in effect body
            const reset = setTimeout(() => setShowSkeleton(false), 0);
            return () => clearTimeout(reset);
        }
        timerRef.current = setTimeout(() => setShowSkeleton(true), delayMs);
        return clearTimer;
    }, [isLoading, delayMs, clearTimer]);

    return showSkeleton;
}
